import './DataSecurityViewContainer.scss';

import { DataSecurityViewQueryParams } from './DataSecurityView';
import { FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { getClosestCardHeader } from '@lib/popups';
import { SonarCard } from '@ui/general';
import { VerticalColumnView } from '@ui/general/VerticalColumnView';

import { AppGroupSelect, AppSelect } from '@ui/applications';
import { DateRangePicker, DateRangePickerParams } from '@ui/DateRangePicker';
import React, { PropsWithChildren, useCallback, useEffect } from 'react';
import { Row, Space } from 'antd';
import { useApplicationReference, useDataSecurityApps, useOnMount, useSonarSelector } from '@hooks';
import { useNavigate, useParams } from 'react-router-dom';

export interface DataSecurityViewContainerProps {
	viewParams: DataSecurityViewQueryParams;
	onViewChange: (params: Partial<DataSecurityViewQueryParams>) => void;
	title: string;
}

export const DataSecurityViewContainer: React.FC<PropsWithChildren<DataSecurityViewContainerProps>> = ({
	children,
	viewParams,
	onViewChange,
	title
}) => {
	const { id } = useParams<{id: string}>();
	const { stakeholderGroupId } = useApplicationReference(Number(id));
	const navigate = useNavigate();
	const apps = useDataSecurityApps(false, true);

	const appsLoaded = useSonarSelector(useDataSecurityApps.selectors.dataExists);

	useOnMount(() => {
		onViewChange({ appGroup: stakeholderGroupId });
	});

	const onAppSelect = useCallback((appId?: number) => {
		navigate(`${FULL_ROUTES.DATA_SECURITY}${appId ? `/${appId}` : ''}`);
	}, [ navigate ]);

	const onDateChange = useCallback((dateRange?: DateRangePickerParams) => {
		onViewChange({ dateRange });
	}, [ onViewChange ]);

	const onAppGroupChange = useCallback((userSelectedGroup?: number) => {
		onViewChange({ userSelectedGroup });
		navigate(FULL_ROUTES.DATA_SECURITY);
	}, [ navigate, onViewChange ]);

	useEffect(() => {
		if (id && appsLoaded && !apps.some((app) => app.applicationId === Number(id))) {
			navigate(FULL_ROUTES.DATA_SECURITY);
		}
	});

	return (
		<VerticalColumnView
			collapseSidebar
			id='data-security-view-container'
			loading={ !appsLoaded }>
			<SonarCard
				className='data-security-view-header'
				extra={
					<Space>
						<AppSelect
							getPopupContainer={ getClosestCardHeader }
							includeDetected
							onChange={ onAppSelect }
							value={ id ? Number(id) : undefined } />
						<AppGroupSelect
							getPopupContainer={ getClosestCardHeader }
							onChange={ onAppGroupChange }
							value={ viewParams.appGroup } />
						<DateRangePicker
							endDate={ viewParams.dateRange.end }
							onChange={ onDateChange }
							startDate={ viewParams.dateRange.start } />
					</Space>
				}
				title={ title } />
			<Row gutter={ 8 }>
				{ children }
			</Row>
		</VerticalColumnView>
	);
};
